
import { computed, defineComponent, PropType, ref, watch } from "vue";
import { EditStore } from "@/stores";
import { ImageFrame } from "@/components/EditDownload.vue";
import InputNumber from "@/components/InputNumber.vue";
import { consts } from "../services/Models";

export enum SizeMode {
  FB,
  FBAD,
  TW,
  INST,
  Per,
  Px,
}
export interface IDownloadSize {
  sizeMode: SizeMode;
  imgSize: { width: number; height: number };
}

export default defineComponent({
  name: "EditDownloadSelectSize",
  components: {
    InputNumber,
  },
  props: {
    editStore: { type: Object as PropType<EditStore> },
    images: { type: Object as PropType<ImageFrame[]> },
    size: { type: Object as PropType<IDownloadSize> },
  },
  setup(props, context) {
    const mode = {
      fb: SizeMode.FB,
      fbad: SizeMode.FBAD,
      tw: SizeMode.TW,
      inst: SizeMode.INST,
      per: SizeMode.Per,
      px: SizeMode.Px,
    };
    const round = Math.round;
    const originalSize = computed(() => {
      // 画像の１枚目を基準にオリジナルサイズを取得
      const img = props.images?.map((x) => x.template).find(() => true);
      if (img === undefined) return consts.originalSize;
      return {
        width: img.naturalWidth,
        height: img.naturalHeight,
      };
    });

    const state = ref<IDownloadSize>({
      sizeMode: props.size?.sizeMode ?? SizeMode.FB,
      imgSize: props.size?.imgSize ?? {
        width: consts.fbSize.width,
        height: consts.fbSize.height,
      },
    });
    const percent = ref(state.value.imgSize.width / originalSize.value.width);

    const updateSize = (per: number) => {
      percent.value = per;
      const width = round(per * originalSize.value.width);
      const height = round(per * originalSize.value.height);
      state.value.imgSize.width = width;
      state.value.imgSize.height = height;
    };
    const updateByPercent = (v: number) => {
      // 以下は特別扱いしてpxが同じになるようにする
      // 90%：Facebook一般向けと同じpxに設定
      // 81%：Facebookカルーセル広告向けと同じpxに設定
      // 96%：Twitter向けと同じpxに設定
      switch (round(v * 100.0)) {
        case 90:
          v = consts.fbSize.width / originalSize.value.width;
          break;
        case 81:
          v = consts.fbadSize.width / originalSize.value.width;
          break;
        case 96:
          v = consts.twSize.width / originalSize.value.width;
          break;
      }
      updateSize(v);
      state.value.sizeMode = SizeMode.Per;
    };
    // 変更後の幅に応じて比率と高さの値を設定する
    const updateByWidth = (v: number) => {
      const p = v / originalSize.value.width;
      updateSize(p);
      state.value.sizeMode = SizeMode.Px;
    };
    // 変更後の高さに応じて比率と幅の値を設定する
    const updateByHeight = (v: number) => {
      const p = v / originalSize.value.height;
      updateSize(p);
      state.value.sizeMode = SizeMode.Px;
    };

    // 丸め処理
    const roundedPercent = computed(() => round(percent.value * 100.0));
    const roundedWidth = computed(() =>
      round(state.value?.imgSize.width ?? consts.originalSize.width)
    );
    const roundedHeight = computed(() =>
      round(state.value?.imgSize.height ?? consts.originalSize.height)
    );

    const changedSizeMode = () => {
      if (state.value === undefined) return;
      switch (state.value.sizeMode) {
        case SizeMode.FB:
          updateSize(consts.fbSize.width / originalSize.value.width);
          break;
        case SizeMode.FBAD:
          updateSize(consts.fbadSize.width / originalSize.value.width);
          break;
        case SizeMode.TW:
          updateSize(consts.twSize.width / originalSize.value.width);
          break;
        case SizeMode.INST:
          updateSize(consts.instSize.width / originalSize.value.width);
          break;
      }
    };
    watch(
      () => state.value?.sizeMode,
      () => changedSizeMode()
    );
    // 次ページへ進む
    const next = () => {
      context.emit("update:size", state.value);
      context.emit("next");
    };

    return {
      mode,
      consts,
      state,
      roundedPercent,
      roundedWidth,
      roundedHeight,
      round,
      updateByPercent,
      updateByWidth,
      updateByHeight,
      originalSize,
      next,
    };
  },
});
